import { Response, apiAbortController } from '@ui/src';
import api from '../../../../api/api';
import {
  CASP,
  CASPCurrency,
  TransactionType,
  Account,
  TransactionTypeAllowedTransfersEnum,
} from '../../types';

export const transactionsTypesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getEmployeesTransactionsTypes: build.query<Response<TransactionType>, void>(
      {
        query: () => ({
          url: `/v1/transactions-types`,
        }),
      },
    ),
    updateEmployeesTransactionsTypes: build.mutation<
      {
        data: TransactionType;
      },
      {
        id: string;
        body: {
          allowedTransfers: TransactionTypeAllowedTransfersEnum[];
          active: boolean;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-types/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getEmployeesCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: `/v1/casps?active=ALL&all=true`,
      }),
    }),
    getCASPItem: build.query<{ data: CASP }, string>({
      query: (id) => ({
        url: `/v1/casps/${id}`,
      }),
    }),
    updateCasp: build.mutation<
      {
        data: CASP;
      },
      { id: string; body: { name: string; active: boolean } }
    >({
      query: (data) => ({
        url: `/v1/casps/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCaspCurrencies: build.mutation<
      {
        data: CASPCurrency[];
      },
      { body: { id: string; active: boolean }[] }
    >({
      query: (data) => ({
        url: '/v1/casps/currencies/status',
        method: 'PUT',
        body: { data: data.body },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCasp: build.mutation<
      {
        data: CASP;
      },
      { name: string; active: boolean }
    >({
      query: (body) => ({
        url: '/v1/casps',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    attachCurrenciesToCASP: build.mutation<
      {
        data: CASPCurrency[];
      },
      {
        caspId: string;
        currencies: {
          currencyId: string;
          active: boolean;
        }[];
      }
    >({
      query: (body) => ({
        url: '/v1/casps/attach',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    toggleTransactionTypeActive: build.mutation<
      {
        data: TransactionType;
      },
      { active: boolean; id: string }
    >({
      query: (data: { active: boolean; id: string }) => ({
        url: `/v1/transactions-types/${data.id}/active`,
        method: 'PUT',
        body: {
          active: data.active,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSettingsAccounts: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetEmployeesTransactionsTypesQuery,
  useToggleTransactionTypeActiveMutation,
  useLazyGetEmployeesCASPQuery,
  useUpdateCaspMutation,
  useCreateCaspMutation,
  useAttachCurrenciesToCASPMutation,
  useUpdateCaspCurrenciesMutation,
  useLazyGetCASPItemQuery,
  useLazyGetSettingsAccountsQuery,
  useUpdateEmployeesTransactionsTypesMutation,
} = transactionsTypesApi;

export const {
  endpoints: {
    getEmployeesTransactionsTypes,
    toggleTransactionTypeActive,
    getEmployeesCASP,
    updateCasp,
    createCasp,
    attachCurrenciesToCASP,
    updateCaspCurrencies,
    getCASPItem,
    getSettingsAccounts,
    updateEmployeesTransactionsTypes,
  },
} = transactionsTypesApi;
